import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Radar, Target, Flame, Trophy, TrendingUp, Zap, Calendar, Clock,
  Bell, ArrowRight, Sparkles, Headphones, RefreshCw, AlertOctagon,
  MapPin, Building2, Brain, Mail, AlertTriangle, CheckCircle2,
  ChevronRight, Activity, MessageCircle, FileText, Gavel, Palette,
} from 'lucide-react';
import {
  getPretenderStats, listPretenderCandidates,
  getFeedStats, listFeed,
  listNotifications,
} from '../lib/api';
import api from '../lib/api';

// #6 ROI summary — uses configured axios api with auth interceptor
const fetchRoiSummary = (days = 30) =>
  api.get(`/dashboard/roi?days=${days}`).then(r => r.data).catch(() => null);
// Command center — stan operacyjny na żywo (T79)
const fetchOps = () =>
  api.get('/dashboard/ops').then(r => r.data).catch(() => null);
import { useAgentFeed } from '../hooks/useAgentFeed';

// ============================================================
// Helpers
// ============================================================

const CAT_META = {
  sense: { label: 'SENSE', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  think: { label: 'THINK', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  act: { label: 'ACT', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  identity: { label: 'IDENTITY', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  other: { label: 'OTHER', color: 'text-muted-foreground', bg: 'bg-muted' },
};

const PRIO_STYLE = {
  critical: 'bg-red-500/20 text-red-300 border-red-500/40',
  high: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  medium: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  low: 'bg-muted/60 text-muted-foreground border-muted',
};

const EVENT_ICONS = {
  'signal.discovered': Radar,
  'candidate.created': Target,
  'candidate.hot': Flame,
  'tender.new': FileText,
  'tender.deadline_soon': Clock,
  'swz.changed': AlertTriangle,
  'consilium.done': Brain,
  'redflag.detected': AlertTriangle,
  'outreach.sent': Mail,
  'agent.decision': Sparkles,
  'agent.briefing': Sparkles,
};

function timeAgo(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return 'przed chwilą';
    if (diff < 3600) return Math.floor(diff / 60) + ' min temu';
    if (diff < 86400) return Math.floor(diff / 3600) + ' h temu';
    return Math.floor(diff / 86400) + ' dni';
  } catch { return ts; }
}

// ============================================================
// BriefingHero
// ============================================================

function BriefingHero({ stats, pretenderStats, loading }) {
  const dateStr = new Date().toLocaleDateString('pl-PL', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const newSignals = (stats?.by_event_type || []).find(e => e._id === 'signal.discovered')?.count || 0;
  const newCandidates = (stats?.by_event_type || []).find(e => e._id === 'candidate.created')?.count || 0;
  const hot = pretenderStats?.hot_candidates || 0;
  const totalCand = pretenderStats?.total_candidates || 0;

  const briefing = loading ? 'Generuję briefing...' : (
    <>
      Przez ostatnie 24h zebrałem <strong className="text-foreground">{newSignals}</strong> nowych sygnałów
      i utworzyłem <strong className="text-foreground">{newCandidates}</strong> nowych kandydatów inwestycyjnych.{' '}
      {hot > 0 ? (
        <><strong className="text-red-400">{hot} kandydatów przekroczyło próg score 60</strong> — rekomenduję spojrzeć na nich w pierwszej kolejności.</>
      ) : (
        <>Żaden kandydat nie przekroczył jeszcze progu "hot" (score ≥ 60), ale {totalCand} jest w obserwacji.</>
      )}{' '}
      Szukam też wstępnych konsultacji rynkowych i przetargów na dokumentację projektową — to najbardziej wiarygodne sygnały wyprzedzające.
    </>
  );

  return (
    <div className="relative overflow-hidden rounded-xl px-4 py-3 border border-primary/20"
         style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(52,211,153,0.04))' }}>
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Dzień dobry
            </h1>
            <span className="text-[11px] uppercase tracking-wider text-primary">{dateStr}</span>
          </div>
          <div className="text-[13px] text-muted-foreground leading-snug max-w-3xl mt-0.5">
            {briefing}
          </div>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// KPI Card
// ============================================================

// BUG #4 fix (2026-05-11): KPI cards z onClick → navigate
// Mapa iconColor → rgb dla glow (design refresh 2026-05-30)
const KPI_GLOW = {
  'text-primary': '20,184,166', 'text-emerald-400': '16,185,129',
  'text-amber-400': '245,158,11', 'text-red-400': '239,68,68',
  'text-fuchsia-400': '217,70,239', 'text-blue-400': '59,130,246',
};

function KPICard({ icon: Icon, label, value, trend, trendColor, iconColor, onClick, emptyHint }) {
  const isClickable = !!onClick;
  const rgb = KPI_GLOW[iconColor] || KPI_GLOW['text-primary'];
  return (
    <Card
      className={`glass-card relative overflow-hidden ${isClickable ? 'cursor-pointer lift-hover hover:border-primary/40' : ''}`}
      onClick={onClick}
      title={isClickable ? 'Klik aby zobaczyć szczegóły' : undefined}
    >
      {/* glow w rogu w kolorze ikony */}
      <div className="absolute -top-6 -right-6 w-24 h-24 pointer-events-none opacity-60"
           style={{ background: `radial-gradient(circle, rgba(${rgb},0.16), transparent 70%)` }} />
      <CardContent className="pt-4 pb-4 relative">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 ${iconColor || 'text-primary'}`} />
          {label}
        </div>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        {/* IMPROV: pomocna podpowiedź gdy wartość = 0 (np. "Brak pilnych decyzji") */}
        {value === 0 && emptyHint && (
          <div className="text-[10px] text-muted-foreground italic mt-1">{emptyHint}</div>
        )}
        {trend && (
          <div className={`text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded ${trendColor || 'text-muted-foreground bg-muted'}`}>
            {trend}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Signal Card (feed item compact)
// ============================================================

function SignalCard({ item, onClick }) {
  const meta = CAT_META[item.category] || CAT_META.other;
  const Icon = EVENT_ICONS[item.event_type] || Radar;
  return (
    <div
      onClick={onClick}
      className={`bg-card border rounded-lg p-3 mb-2 hover:border-primary/40 transition cursor-pointer ${!item.read ? 'border-l-2 border-l-primary' : 'border-border'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.bg}`}>
          <Icon className={`w-4 h-4 ${meta.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium line-clamp-2">{item.title}</div>
          {item.summary && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{item.summary}</div>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Badge variant="outline" className={`${PRIO_STYLE[item.priority] || ''} text-xs`}>
              {item.priority}
            </Badge>
            <span className="truncate">{item.event_type}</span>
            <span className="ml-auto shrink-0">{timeAgo(item.ts)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Decision Card
// ============================================================

function DecisionCard({ title, summary, action, onAction, onDismiss }) {
  return (
    <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg p-3 mb-2 hover:border-amber-500/50 transition">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
          <AlertOctagon className="w-4 h-4 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium line-clamp-2">{title}</div>
          {summary && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{summary}</div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" onClick={onAction}>{action}</Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>Później</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Candidate Mini Card
// ============================================================

function CandidateMiniCard({ cand, onClick }) {
  const score = Math.round(cand.score || 0);
  const prob = Math.round((cand.probability_12m || 0) * 100);
  return (
    <div
      onClick={onClick}
      className="bg-card border border-border rounded-lg p-3 hover:border-primary/40 transition cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium line-clamp-2">{cand.title}</div>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            {cand.region && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />{cand.region}
              </span>
            )}
            {cand.entity_name && (
              <span className="flex items-center gap-1 truncate">
                <Building2 className="w-3 h-3" />
                {cand.entity_name.slice(0, 30)}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-bold">{score}</div>
          <div className="text-xs text-muted-foreground">{prob}% w 12m</div>
        </div>
      </div>
      <Progress value={score} className="h-1" />
      <div className="flex flex-wrap gap-1 mt-2">
        {(cand.signal_types_seen || []).slice(0, 3).map(t => (
          <Badge key={t} variant="outline" className="text-xs">
            {t.replace('_', ' ')}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Mini Map Panel
// ============================================================

function MiniMapPanel({ pretenderStats, onFull }) {
  const regions = (pretenderStats?.by_region || []).filter(r => r._id);
  const max = Math.max(1, ...regions.map(r => r.count));

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-400" />
            Mapa sygnałów PL
          </div>
          <Button variant="ghost" size="sm" onClick={onFull}>
            Full map <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        {regions.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">
            Mapa wypełni się gdy Adam zbierze sygnały.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Top regiony</div>
              {regions.slice(0, 7).map(r => (
                <div key={r._id} className="flex items-center gap-2 py-1 text-xs">
                  <div className="w-24 text-muted-foreground truncate">{r._id}</div>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(r.count / max) * 100}%`,
                        background: 'linear-gradient(90deg, #3b82f6, #14b8a6)',
                      }}
                    />
                  </div>
                  <div className="w-8 text-right font-medium">{r.count}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center bg-muted/30 rounded-lg">
              <svg viewBox="0 0 200 160" className="w-full max-w-[200px] opacity-40">
                <g fill="currentColor" className="text-primary" fillOpacity="0.35" stroke="currentColor" strokeWidth="0.3" strokeOpacity="0.5">
                  <rect x="60" y="10" width="70" height="30" />
                  <rect x="40" y="45" width="50" height="30" />
                  <rect x="95" y="45" width="50" height="30" />
                  <rect x="20" y="80" width="40" height="35" />
                  <rect x="65" y="80" width="40" height="35" />
                  <rect x="110" y="80" width="45" height="35" />
                  <rect x="40" y="120" width="50" height="30" />
                  <rect x="95" y="120" width="50" height="30" />
                </g>
              </svg>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// FAB Ask Adam
// ============================================================

function AskAdamFAB({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Zapytaj Adama (Shift+A) — placeholder, Faza 9.7"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-xl hover:scale-105 transition-all"
      style={{ boxShadow: '0 10px 30px rgba(20,184,166,0.4), 0 4px 8px rgba(0,0,0,0.3)' }}
    >
      <MessageCircle className="w-6 h-6" />
    </button>
  );
}

// ============================================================
// Funnel pipeline — realne etapy po progach score (T50, styl Kanban z infografiki)
// ============================================================
function FunnelStrip({ funnel, loading, onStage }) {
  const f = funnel || {};
  const stages = [
    { key: 'signals',    label: 'Sygnały',       sub: 'wykryte',     rgb: '59,130,246',  val: f.signals },
    { key: 'candidates', label: 'Kandydaci',     sub: 'utworzeni',   rgb: '20,184,166',  val: f.candidates },
    { key: 'qualified',  label: 'Kwalifikowani', sub: 'score ≥ 40',  rgb: '245,158,11',  val: f.qualified },
    { key: 'hot',        label: 'Hot',           sub: 'score ≥ 60',  rgb: '239,68,68',   val: f.hot },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {stages.map((s, i) => (
        <button key={s.key} onClick={() => onStage && onStage(s.key)}
                className="glass-card lift-hover rounded-xl p-3 text-left relative overflow-hidden"
                data-testid={`funnel-${s.key}`}>
          <div className="absolute -top-6 -right-6 w-24 h-24 pointer-events-none opacity-60"
               style={{ background: `radial-gradient(circle, rgba(${s.rgb},0.16), transparent 70%)` }} />
          <div className="relative">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: `rgb(${s.rgb})`, boxShadow: `0 0 6px rgba(${s.rgb},0.8)` }} />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.sub}</span>
            </div>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              {loading ? '—' : (s.val ?? 0).toLocaleString('pl-PL')}
            </div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================================================
// Top status bar — badge'e produktu + health (T50, styl infografiki)
// ============================================================
function TopStatusBar({ healthy }) {
  const Dot = ({ rgb }) => (
    <span className="w-1.5 h-1.5 rounded-full inline-block"
          style={{ background: `rgb(${rgb})`, boxShadow: `0 0 6px rgba(${rgb},0.85)` }} />
  );
  return (
    <div className="flex flex-wrap items-center gap-2 justify-end" data-testid="home-status-bar">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium glass-card text-teal-300"
            style={{ fontFamily: 'Space Grotesk' }}>
        <Dot rgb="20,184,166" /> B2B · przetargi i inwestycje
      </span>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium glass-card text-blue-300"
            style={{ fontFamily: 'Space Grotesk' }}>
        <Building2 className="w-3 h-3" /> Multi-tenant SaaS
      </span>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold glass-card ${healthy ? 'text-emerald-300' : 'text-red-300'}`}
            style={{ fontFamily: 'Space Grotesk' }}>
        <Dot rgb={healthy ? '16,185,129' : '239,68,68'} /> Health: {healthy ? 'GREEN' : 'CHECK'}
      </span>
    </div>
  );
}

// Command Center — operacyjny stan systemu na żywo (T79)
function CommandCenter({ ops, navigate }) {
  const Tile = ({ icon: Icon, rgb, label, value, sub, onClick, alert }) => (
    <button
      onClick={onClick}
      className="group relative overflow-hidden rounded-xl p-3 text-left border transition-all duration-200 hover:scale-[1.01]"
      style={{ borderColor: `rgba(${rgb},0.30)`, background: `linear-gradient(135deg, rgba(${rgb},0.10), rgba(${rgb},0.02))` }}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `rgba(${rgb},0.15)` }}>
          <Icon className="w-4 h-4" style={{ color: `rgb(${rgb})` }} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-lg font-bold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>{value}</div>
          <div className={`text-[11px] truncate ${alert ? 'text-amber-300' : 'text-muted-foreground'}`}>{sub}</div>
        </div>
      </div>
    </button>
  );

  if (!ops) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[0,1,2,3].map(i => <Skeleton key={i} className="h-[68px] w-full rounded-xl" />)}
    </div>;
  }

  const b = ops.bridge || {}, l = ops.loops || {}, o = ops.opportunities_24h || {}, a = ops.api_usage || {};
  const sphinxUsd = a.sphinx?.cost_usd;
  const apiSub = [
    sphinxUsd != null ? `SPHINX $${sphinxUsd}` : null,
    a.serper?.searches_est != null ? `Serper ~${a.serper.searches_est}` : null,
    a.llm?.triage_runs_24h != null ? `LLM ${a.llm.triage_runs_24h}×` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="command-center">
      <Tile icon={Activity} rgb="37,99,235" label="Most SPHINX"
        value={b.total_in_base ?? 0}
        sub={b.last_sync ? `pull ${timeAgo(b.last_sync)} · +${b.last_inserted || 0} nowych` : 'brak pulla'}
        onClick={() => navigate('/tenders?sort=newest')} />
      <Tile icon={RefreshCw} rgb="52,211,153" label="Pętle autonomiczne"
        value={`${l.active_count ?? 0} / ${l.paused_count ?? 0}`}
        sub={l.last_run_at ? `ostatni: ${l.last_run_task || '—'} ${timeAgo(l.last_run_at)}` : 'brak runów'}
        alert={(l.paused_count || 0) > 0}
        onClick={() => navigate('/automations')} />
      <Tile icon={Radar} rgb="14,165,233" label="Nowe okazje 24h"
        value={(o.tenders ?? 0) + (o.investments ?? 0)}
        sub={`${o.tenders ?? 0} przetargów · ${o.investments ?? 0} inwest.`}
        onClick={() => navigate('/tenders?sort=newest')} />
      <Tile icon={Zap} rgb="245,158,11" label="Koszt API dziś"
        value={sphinxUsd != null ? `$${sphinxUsd}` : '—'}
        sub={apiSub || 'brak danych'}
        alert={(sphinxUsd || 0) > 5}
        onClick={() => navigate('/reports')} />
    </div>
  );
}

// ============================================================
// Main Home v3
// ============================================================

// Role z localStorage — Command Center widzą tylko właściciel/admin (dane kosztowe + ops)
function _userRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role) || 'viewer'; }
  catch { return 'viewer'; }
}
const OWNER_ROLES = ['owner', 'admin', 'platform_owner'];

export default function HomeV3({ lang, tenantId }) {
  const navigate = useNavigate();
  const isOwner = OWNER_ROLES.includes(_userRole());
  const [feedStats, setFeedStats] = useState(null);
  const [pretenderStats, setPretenderStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [notifications, setNotifications] = useState({ unread: 0, items: [] });
  const [roi, setRoi] = useState(null);
  const [ops, setOps] = useState(null);  // T79 — command center
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(false);  // T50 — health badge

  const { lastEvent } = useAgentFeed();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fs, ps, fl, cands, notifs, roiData, opsData] = await Promise.all([
        getFeedStats({ since_hours: 24 }).catch(() => ({})),
        getPretenderStats().catch(() => ({})),
        listFeed({ limit: 40, since_hours: 168 }).catch(() => ({ items: [] })),
        listPretenderCandidates({ page_size: 5 }).catch(() => ({ items: [] })),
        listNotifications({ limit: 10 }).catch(() => ({ unread: 0, items: [] })),
        fetchRoiSummary(30),
        isOwner ? fetchOps() : Promise.resolve(null),
      ]);
      setFeedStats(fs);
      setPretenderStats(ps);
      setFeed(fl.items || []);
      setCandidates(cands.items || []);
      setNotifications({ unread: notifs.unread || 0, items: notifs.items || [] });
      setRoi(roiData);
      setOps(opsData);
      setDataError(false);
    } catch (e) {
      setDataError(true);
      toast.error('Błąd ładowania: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const int = setInterval(fetchAll, 60000);
    return () => clearInterval(int);
  }, [fetchAll]);

  // React to live events (WebSocket)
  useEffect(() => {
    if (!lastEvent) return;
    // Just increment unread count optimistically
    setNotifications(n => ({ ...n, unread: n.unread + 1 }));
  }, [lastEvent]);

  // Split feed into 3 columns
  const newSignals = feed.filter(i => i.event_type === 'signal.discovered').slice(0, 6);
  const hotDecisions = feed.filter(i => i.event_type === 'candidate.hot' || i.priority === 'critical').slice(0, 5);
  const actions = feed.filter(i => i.category === 'act' || i.event_type === 'candidate.created').slice(0, 6);

  // Critical alerts
  const criticalAlerts = feed.filter(i => i.priority === 'critical').slice(0, 3);

  const totalCand = pretenderStats?.total_candidates || 0;
  const hot = pretenderStats?.hot_candidates || 0;
  const decisionsCount = notifications.unread;

  return (
    <div className="space-y-5 max-w-[1440px]">
      <TopStatusBar healthy={!dataError} />

      {/* T79 — Command Center: operacyjny stan na żywo. WŁAŚCICIEL/ADMIN ONLY (koszty + ops). */}
      {isOwner && <CommandCenter ops={ops} navigate={navigate} />}

      <BriefingHero stats={feedStats} pretenderStats={pretenderStats} loading={loading} />

      {/* 3 PIPELINE'Y — styl infografiki (A=emerald, B=amber, C=fuchsia) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { key: 'A', label: 'Przetargi', sub: 'BZP / TED', icon: Gavel,
            rgb: '16,185,129', text: 'text-emerald-300', route: '/tenders?sort=match' },
          { key: 'B', label: 'Inwestycje', sub: 'GUNB + decision chain', icon: Building2,
            rgb: '245,158,11', text: 'text-amber-300', route: '/investments' },
          { key: 'C', label: 'Projektanci wnętrz', sub: 'Discovery + kontakty', icon: Palette,
            rgb: '217,70,239', text: 'text-fuchsia-300', route: '/interior-designers' },
        ].map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.key}
              onClick={() => navigate(p.route)}
              className="group relative overflow-hidden rounded-xl p-4 text-left border transition-all duration-200 hover:scale-[1.015]"
              style={{
                borderColor: `rgba(${p.rgb},0.30)`,
                background: `linear-gradient(135deg, rgba(${p.rgb},0.10), rgba(${p.rgb},0.02))`,
              }}
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 pointer-events-none transition-opacity group-hover:opacity-100 opacity-70"
                   style={{ background: `radial-gradient(circle, rgba(${p.rgb},0.18), transparent 70%)` }} />
              <div className="relative flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: `rgba(${p.rgb},0.15)` }}>
                  <Icon className={`w-5 h-5 ${p.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold ${p.text}`}>PIPELINE {p.key}</span>
                  </div>
                  <div className="text-sm font-semibold leading-tight">{p.label}</div>
                  <div className="text-[11px] text-muted-foreground">{p.sub}</div>
                </div>
                <ChevronRight className={`w-4 h-4 ${p.text} opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Funnel — realne etapy lejka (T50) */}
      <FunnelStrip
        funnel={pretenderStats?.funnel}
        loading={loading}
        onStage={(stage) => {
          if (stage === 'hot') navigate('/pre-tender');
          else if (stage === 'qualified') navigate('/tenders?min_score=40&sort=match');
          else navigate('/pre-tender');
        }}
      />

      {/* Critical strip */}
      {criticalAlerts.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="pt-3 pb-3 flex items-center gap-3">
            <AlertOctagon className="w-5 h-5 text-red-400" />
            <div className="flex-1 text-sm">
              <strong>{criticalAlerts.length} krytycznych alertów</strong>
              {' — '}{criticalAlerts[0].title.slice(0, 100)}
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/feed?priority=critical')}>
              Zobacz
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPI grid — BUG #4 fix (2026-05-11): wszystkie 4 karty klikalne z navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard
          icon={Target}
          iconColor="text-primary"
          label="Aktywne szanse"
          value={loading ? '—' : totalCand}
          trend={<><Activity className="w-3 h-3" /> live</>}
          trendColor="text-primary bg-primary/10"
          onClick={() => navigate('/tenders?min_score=40&sort=match')}
        />
        <KPICard
          icon={Zap}
          iconColor="text-amber-400"
          label="Do akcji dziś"
          value={loading ? '—' : decisionsCount}
          trend={<><Clock className="w-3 h-3" /> wymaga decyzji</>}
          trendColor="text-amber-400 bg-amber-500/10"
          onClick={() => navigate('/feed?priority=critical')}
          emptyHint="Brak pilnych decyzji — wszystko pod kontrolą"
        />
        <KPICard
          icon={Flame}
          iconColor="text-red-400"
          label="Hot candidates"
          value={loading ? '—' : hot}
          trend={<>score ≥ 60</>}
          trendColor={hot > 0 ? 'text-red-400 bg-red-500/10' : 'text-muted-foreground bg-muted'}
          onClick={() => navigate('/pre-tender')}
        />
        <KPICard
          icon={Trophy}
          iconColor="text-emerald-400"
          label="ROI 30d"
          value={
            loading ? '—' :
            roi == null ? '—' :
            roi.roi_ratio == null ? '—' :
            roi.roi_ratio === 0 ? '0×' :
            roi.roi_ratio > 1000 ? `${(roi.roi_ratio/1000).toFixed(0)}k×` :
            `${roi.roi_ratio}×`
          }
          trend={
            loading || !roi ? <>ładuję…</> :
            <>
              {(roi.revenue_pln_period || 0).toLocaleString()} PLN przychód
              {' · '}
              {(roi.cost_estimated_pln || 0).toFixed(2)} PLN koszt
            </>
          }
          onClick={() => navigate('/reports')}
          trendColor={
            roi?.roi_ratio > 100 ? 'text-emerald-400 bg-emerald-500/10' :
            roi?.roi_ratio > 0 ? 'text-amber-400 bg-amber-500/10' :
            'text-muted-foreground bg-muted'
          }
        />
      </div>

      {/* 3 kolumny */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Column 1: Signals */}
        <section>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold pb-2 pl-1 flex items-center gap-2">
            <Radar className="w-3.5 h-3.5 text-blue-400" />
            Nowe sygnały
            <span className="ml-auto px-2 py-0.5 rounded-full bg-muted text-xs">{newSignals.length}</span>
          </div>
          {loading ? (
            <>
              <Skeleton className="h-20 mb-2" />
              <Skeleton className="h-20 mb-2" />
            </>
          ) : newSignals.length === 0 ? (
            <Card>
              <CardContent className="pt-6 pb-6 text-center text-muted-foreground text-sm">
                <Radar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Brak nowych sygnałów
                <div className="mt-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/pre-tender')}>
                    Zbierz teraz
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            newSignals.map(item => (
              <SignalCard key={item.id || item.ts} item={item} onClick={() => navigate('/feed')} />
            ))
          )}
        </section>

        {/* Column 2: Decisions */}
        <section>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold pb-2 pl-1 flex items-center gap-2">
            <AlertOctagon className="w-3.5 h-3.5 text-amber-400" />
            Wymaga twojej decyzji
            <span className="ml-auto px-2 py-0.5 rounded-full bg-muted text-xs">{hotDecisions.length}</span>
          </div>
          {loading ? (
            <Skeleton className="h-20 mb-2" />
          ) : hotDecisions.length === 0 ? (
            <Card>
              <CardContent className="pt-6 pb-6 text-center text-muted-foreground text-sm">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50 text-emerald-400" />
                Brak pilnych decyzji
                <div className="mt-1 text-xs">
                  Gdy kandydat osiągnie score 60, pojawi się tu.
                </div>
              </CardContent>
            </Card>
          ) : (
            hotDecisions.map(item => (
              <DecisionCard
                key={item.id || item.ts}
                title={item.title}
                summary={item.summary}
                action="Analizuj"
                onAction={() => navigate('/pre-tender')}
                onDismiss={() => {}}
              />
            ))
          )}
        </section>

        {/* Column 3: Actions */}
        <section>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold pb-2 pl-1 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            Co Adam zrobił
            <span className="ml-auto px-2 py-0.5 rounded-full bg-muted text-xs">{actions.length}</span>
          </div>
          {loading ? (
            <Skeleton className="h-20 mb-2" />
          ) : actions.length === 0 ? (
            <Card>
              <CardContent className="pt-6 pb-6 text-center text-muted-foreground text-sm">
                Brak akcji w tym oknie.
              </CardContent>
            </Card>
          ) : (
            actions.map(item => (
              <SignalCard key={item.id || item.ts} item={item} onClick={() => navigate('/feed')} />
            ))
          )}
        </section>
      </div>

      {/* Bottom row: Mini map + Top candidates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MiniMapPanel pretenderStats={pretenderStats} onFull={() => navigate('/pre-tender')} />

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Top kandydaci
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/pre-tender')}>
                Wszyscy <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-6">
                Brak kandydatów. <Button variant="link" size="sm" onClick={() => navigate('/pre-tender')}>Pre-Tender →</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {candidates.slice(0, 5).map(c => (
                  <CandidateMiniCard
                    key={c.id}
                    cand={c}
                    onClick={() => navigate('/pre-tender')}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AskAdamFAB onClick={() => toast.info('Ask Adam chat — Faza 9.7 (Command Palette)')} />
    </div>
  );
}
